import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getClosestPollingRateRoute } from "../utils/get-closest-polling-rate-route";
import { ROUTES_POLLING_RATE_CONFIG } from "../routing/routes-polling-rate-config";

/**
 * Hook that reactively returns the polling rate for the current route.
 * Automatically updates when the route changes.
 *
 * The hook matches the current pathname to the closest configured route in
 * ROUTES_POLLING_RATE_CONFIG and returns the corresponding polling rate in milliseconds.
 *
 * @returns The polling rate in milliseconds for the current route
 *
 * @example
 * function MyPollingComponent() {
 *   const pollingRateMs = usePollingRate();
 *
 *   useEffect(() => {
 *     const interval = setInterval(() => {
 *       // Fetch fresh data
 *       refetchData();
 *     }, pollingRateMs);
 *
 *     return () => clearInterval(interval);
 *   }, [pollingRateMs, refetchData]);
 *
 *   return <div>Polling every {pollingRateMs}ms</div>;
 * }
 *
 * @example
 * // On /messaging route: returns 5000 (5 seconds)
 * // On /dashboard route: returns 5000 (5 seconds)
 * // On /tickets route: returns 10000 (10 seconds - DEFAULT)
 * // On /messaging/inbox/sms/thread-123: returns 5000 (5 seconds - matches /messaging)
 */
export const usePollingRate = (): number => {
  const location = useLocation();

  const pollingRate = useMemo(() => {
    const routeKey = getClosestPollingRateRoute(location.pathname);
    return ROUTES_POLLING_RATE_CONFIG[routeKey];
  }, [location.pathname]);

  return pollingRate;
};
